import {
  Box,
  CloseButton,
  HStack,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import {useSidePanelPanier} from './SidePanelPanierContext';
import {
  groupLignesParCategorie,
  sortLignesDePanierParDate,
  useLivraisonsPossiblesContext,
  usePanier,
} from 'ff-common';
import {TriSelection} from 'api-types/ffconnect';
import {HaliRenderMany, useHalState, useHalStates} from '@synako/haligator';
import {useTranslation} from 'react-i18next';
import {SidePanelLignePanier} from './SidePanelLignePanier';
import {useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {useClient} from '@/haligator-factory';
import {Navigable} from '@synako/halitrek';
import {PanierDesc} from 'api-types/ffconnect-hal';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {
  routes,
  toDecimalFr,
  useAffichagePrixTtc,
  usePanierCommands,
} from 'ff-common';
import {TrashIcon} from '@/components/svg/TrashIcon';
import {IconButton} from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import {ModaleAlerteConfirmation} from '@/domains/panier/PanierFound/ModaleAlerteConfirmation';
import {MargesCommande} from '@/domains/panier/MargesCommande';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {useNavigatePanier} from '@/hooks/panier/useNavigatePanier';
import {HEADER_HEIGHT} from '@/constants/layout';
import {getModeTheme} from '@/utils/appMode';
import {PdfBadge} from '@/domains/panier/components/PdfBadge';

// Largeur du side panel panier
export const SIDE_PANEL_WIDTH = '385px';

// Composant principal du side panel panier, qui affiche le contenu du panier (FF ou marketplace selon le contexte)
export const SidePanelPanier: React.FC = () => {
  const {isOpen, close} = useSidePanelPanier();
  const {t} = useTranslation();
  // Nécessaire pour que le changement de quantité marketplace rafraîchisse le bon panier dans le contexte
  const {selectedMarketplaceId} = useLivraisonsPossiblesContext();

  if (!isOpen) return null;

  return (
    <Box
      w={{base: '100%', lg: SIDE_PANEL_WIDTH}} // Plein écran en base/md, largeur fixe en lg+
      minW={{lg: SIDE_PANEL_WIDTH}} // minW uniquement en lg+ (en base/md, position fixed)
      position={{base: 'fixed', lg: 'sticky'}} // Fixed en plein écran pour se superposer, sticky en lg+
      top={HEADER_HEIGHT.md}
      right={{base: '0', lg: 'unset'}}
      h={`calc(100vh - ${HEADER_HEIGHT.md})`}
      bg='white'
      boxShadow='-4px 0 12px rgba(0, 0, 0, 0.08)'
      ml='-1px' // Compense le border du séparateur navbar pour alignement parfait
      display='flex'
      flexDirection='column'
      overflow='hidden'
      pb={{base: '4.5rem', md: '0'}} // Compense la BottomNav en mobile
      zIndex={{base: 'overlay', lg: 'dropdown'}}
      // Animation slide-in depuis la droite
      sx={{
        '@keyframes slideInFromRight': {
          from: {transform: 'translateX(100%)'},
          to: {transform: 'translateX(0)'},
        },
        '@keyframes slideInSide': {
          from: {minWidth: '0', width: '0', opacity: 0},
          to: {},
        },
        animation: {
          base: 'slideInFromRight 0.5s ease-out',
          lg: 'slideInSide 0.5s ease-out',
        },
      }}
    >
      {selectedMarketplaceId ? (
        <SidePanelMarketplaceContent
          t={t}
          close={close}
        />
      ) : (
        <SidePanelFFContent
          t={t}
          close={close}
        />
      )}
    </Box>
  );
};

// Contenu du side panel pour un panier France Frais
const SidePanelFFContent: React.FC<{
  t: ReturnType<typeof useTranslation>['t'];
  close: () => void;
}> = ({t, close}) => {
  const panierNav = usePanier();
  const {data: panierData, state: panierState} = useHalState(panierNav);
  const {livraisonSelectionnee} = useLivraisonsPossiblesContext();
  const navigate = useNavigate();
  const {buildClientRoute} = useClientRoute();
  const isPanierPdf = panierState?.hasOneLink('processus') ?? false;

  return (
    <>
      <SidePanelHeader
        t={t}
        nombre_de_lignes={panierData?.nombre_de_lignes}
        close={close}
        isPanierPdf={isPanierPdf}
      />
      <Box
        flex='1'
        overflowY='auto'
        p='0'
      >
        {panierData ? (
          <HaliRenderMany
            key={`${panierData.id}:${panierData.nombre_de_lignes}`}
            many={panierNav.followAll('lignes_de_panier')}
            skeleton={() =>
              Array.from({length: panierData.nombre_de_lignes}, (_, idx) => (
                <Skeleton
                  key={idx}
                  h='100px'
                  m='3'
                />
              ))
            }
            render={({states}) => (
              <SidePanelLignesContent
                lignesDePanier={states}
                triSelection={panierData.tri_selection_panier}
              />
            )}
          />
        ) : (
          <SidePanelEmpty t={t} />
        )}
      </Box>
      {panierData && panierData.nombre_de_lignes > 0 && (
        <SidePanelFooter
          total_estime_ht={panierData.total_estime_ht ?? 0}
          total_estime_ttc={panierData.total_estime_ttc}
          panierNav={panierNav}
          close={close}
          showMarges
          onFinalize={() => {
            close();
            navigate(
              buildClientRoute(routes.client.panier_redirect, {
                date_de_livraison:
                  livraisonSelectionnee.data.tolede.date_de_livraison,
              }),
            );
          }}
        />
      )}
    </>
  );
};

// Contenu du side panel pour un panier marketplace
const SidePanelMarketplaceContent: React.FC<{
  t: ReturnType<typeof useTranslation>['t'];
  close: () => void;
}> = ({t, close}) => {
  const client = useClient();
  const {selectedMarketplaceId, setSelectedMarketplaceId} =
    useLivraisonsPossiblesContext();
  const navigate = useNavigate();
  const {buildClientRoute} = useClientRoute();

  const paniersNav = client.follow('paniers', {
    type_panier: selectedMarketplaceId!,
    statut: 'ouvert',
  });
  const paniersHook = useHalStates(paniersNav.followAll('items'));
  const panierState = paniersHook.states?.[0];

  return (
    <>
      <SidePanelHeader
        t={t}
        nombre_de_lignes={panierState?.data.nombre_de_lignes}
        close={close}
      />
      <Box
        flex='1'
        overflowY='auto'
        p='0'
      >
        {panierState ? (
          <SidePanelPanierBody
            panierNav={panierState}
            panierData={panierState.data}
          />
        ) : (
          <SidePanelEmpty t={t} />
        )}
      </Box>
      {panierState && panierState.data.nombre_de_lignes > 0 && (
        <SidePanelFooter
          total_estime_ht={panierState.data.total_estime_ht ?? 0}
          total_estime_ttc={panierState.data.total_estime_ttc}
          panierNav={panierState}
          close={close}
          onFinalize={() => {
            close();
            navigate(
              buildClientRoute(routes.client.panier_marketplace, {
                marketplace_id: selectedMarketplaceId!,
              }),
            );
          }}
          onVider={() => {
            setSelectedMarketplaceId(null);
          }}
        />
      )}
    </>
  );
};

// Body partagé : affiche les lignes d'un panier (FF ou marketplace)
const SidePanelPanierBody: React.FC<{
  panierNav: Navigable<PanierDesc>;
  panierData: {id: string; nombre_de_lignes: number};
}> = ({panierNav, panierData}) => {
  return (
    <HaliRenderMany
      key={`${panierData.id}:${panierData.nombre_de_lignes}`}
      many={panierNav.followAll('lignes_de_panier')}
      skeleton={() =>
        Array.from({length: panierData.nombre_de_lignes}, (_, idx) => (
          <Skeleton
            key={idx}
            h='100px'
            m='3'
          />
        ))
      }
      render={({states}) => <SidePanelLignesContent lignesDePanier={states} />}
    />
  );
};

// Header partagé
const SidePanelHeader: React.FC<{
  t: ReturnType<typeof useTranslation>['t'];
  nombre_de_lignes?: number;
  close: () => void;
  isPanierPdf?: boolean;
}> = ({t, nombre_de_lignes, close, isPanierPdf}) => (
  <HStack
    borderBottom='1px solid'
    borderColor='gray.light'
    h='56px'
    px='4'
    justifyContent='space-between'
    alignItems='center'
    flexShrink={0}
  >
    <HStack
      alignItems='center'
      gap='3'
    >
      <Text
        fontSize='lg'
        fontWeight='700'
      >
        {t('panier.panier')}
      </Text>

      {/* Badge PDF */}
      {isPanierPdf && <PdfBadge />}

      {nombre_de_lignes !== undefined && (
        <Text
          fontSize='sm'
          color='gray.dark'
        >
          ({t('global.produit', {count: nombre_de_lignes})})
        </Text>
      )}
    </HStack>
    <CloseButton
      onClick={close}
      bg='gray.lighter'
      borderRadius='full'
      _hover={{bg: 'gray.light'}}
    />
  </HStack>
);

// Footer partagé avec bouton "Vider" + "Finaliser la commande"
const SidePanelFooter: React.FC<{
  total_estime_ht: number;
  total_estime_ttc: number; // Affiché à la place du HT quand l'affichage des prix TTC est activé pour ce client
  panierNav: Navigable<PanierDesc>;
  close: () => void;
  onFinalize: () => void;
  onVider?: () => void; // Callback supplémentaire après le vider (ex: reset marketplace)
  showMarges?: boolean; // Affiche la marge à la commande sous le libellé (mode commercial)
}> = ({
  total_estime_ht,
  total_estime_ttc,
  panierNav,
  close,
  onFinalize,
  onVider,
  showMarges,
}) => {
  const {t} = useTranslation();
  const affichagePrixTtc = useAffichagePrixTtc(useClient());
  const totalAffiche = affichagePrixTtc ? total_estime_ttc : total_estime_ht;
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const navigatePanier = useNavigatePanier();
  const {modeColor} = getModeTheme();
  const {vider} = usePanierCommands(
    panierNav,
    textToast,
    onError,
    navigatePanier,
  );

  return (
    <HStack
      borderTop='1px solid'
      borderColor='gray.light'
      p='3'
      bg='white'
      flexShrink={0}
      gap='3'
    >
      <IconButton
        aria-label={t('panier.vider_le_panier')}
        icon={<TrashIcon height='5' />}
        variant='outline'
        borderRadius='10px'
        borderColor='gray.light'
        size='md'
        onClick={() => {
          NiceModal.show(ModaleAlerteConfirmation, {
            validate_texte: t('panier.supprimer_panier_action'),
            body: t('panier.supprimer_panier_confirm'),
            validateAction: () => {
              vider.submit({} as never);
              onVider?.();
              close();
            },
          });
        }}
        isDisabled={vider.isDisabled}
      />
      <HStack
        flex='1'
        bg={modeColor('blue.main', 'red.assistance')}
        borderRadius='10px'
        p='2'
        cursor='pointer'
        _hover={{bg: modeColor('blue.dark', 'red.assistance_prix')}}
        transition='all 0.2s ease'
        onClick={onFinalize}
        justifyContent='space-between'
      >
        <VStack
          align='flex-start'
          spacing='0'
        >
          <Text
            color='white'
            fontWeight='700'
            fontSize='sm'
          >
            {t('panier.finaliser_commande')}
          </Text>
          {showMarges && (
            <MargesCommande
              panierNav={panierNav}
              color='white'
              fontSize='10px'
            />
          )}
        </VStack>
        <Text
          color='white'
          bg={modeColor('blue.deep', 'red.assistance_prix')}
          p='5px'
          px='8px'
          borderRadius='8px'
          fontWeight='700'
          fontSize='sm'
        >
          {toDecimalFr(totalAffiche)}&nbsp;€
        </Text>
      </HStack>
    </HStack>
  );
};

// Message panier vide
const SidePanelEmpty: React.FC<{t: ReturnType<typeof useTranslation>['t']}> = ({
  t,
}) => (
  <VStack
    p='6'
    alignItems='center'
    justifyContent='center'
    h='200px'
  >
    <Text color='gray.dark'>{t('panier.panier_vide')}</Text>
  </VStack>
);

// Affichage des lignes du panier, groupées par catégorie si tri_selection_panier disponible
const SidePanelLignesContent: React.FC<{
  lignesDePanier: Parameters<typeof sortLignesDePanierParDate>[0];
  triSelection?: TriSelection;
}> = ({lignesDePanier, triSelection}) => {
  // Grouper les lignes par catégorie en suivant l'ordre de tri_selection_panier
  const categories = useMemo(
    () =>
      triSelection && triSelection.length > 0
        ? groupLignesParCategorie(
            lignesDePanier,
            triSelection,
            (ligne) => ligne.data.code_article,
          )
        : undefined,
    [lignesDePanier, triSelection],
  );

  // Si pas de catégorisation, afficher les lignes triées par date
  if (!categories) {
    const lignesTriees = sortLignesDePanierParDate(lignesDePanier);
    return (
      <Box>
        {lignesTriees.map((ligneDePanier) => (
          <SidePanelLignePanier
            key={ligneDePanier.data.id}
            ligneDePanier={ligneDePanier}
          />
        ))}
      </Box>
    );
  }

  // Grouper les lignes par catégorie en suivant l'ordre de tri_selection_panier
  return (
    <Box p='8px'>
      {categories.map(({categorie_id, libelle_web, lignes}) => (
        <Box key={categorie_id}>
          {/* En-tête catégorie */}
          <HStack
            bg='gray.150'
            px='4'
            py='2'
            borderColor='gray.light'
            borderRadius='8px'
            mt='2'
          >
            <Text
              fontSize='sm'
              fontWeight='600'
            >
              {libelle_web}
            </Text>
            <Text
              fontSize='sm'
              fontWeight='600'
              color='gray.dark'
              bg='white'
              px='2'
              borderRadius='4px'
            >
              {lignes.length}
            </Text>
          </HStack>
          {/* Lignes de la catégorie */}
          {lignes.map((ligneDePanier) => (
            <SidePanelLignePanier
              key={ligneDePanier.data.id}
              ligneDePanier={ligneDePanier}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};
